import { Article } from '../types';

export const stripePaymentArticle: Article = {
  id: 'stripe-payment-processing',
  title: 'Payment Processing with Stripe',
  slug: 'payment-processing',
  description: 'Complete guide to setting up and using Stripe payment processing for online payments, including setup, configuration, and troubleshooting',
  category: 'finances',
  tags: ['payments', 'stripe', 'online payments', 'credit cards', 'processing', 'integration'],
  lastUpdated: new Date('2024-01-28'),
  readTime: 15,
  content: `
    <h2>Setting Up Stripe Payment Processing</h2>
    <p>Stripe integration allows you to accept credit card payments directly through your CRM, making it easier for customers to pay invoices online and improving your cash flow. This comprehensive guide walks you through the entire setup and management process.</p>

    <div class="bg-blue-50 p-4 rounded-lg mt-4 mb-6">
      <strong>What You'll Need:</strong> 
      <ul>
        <li>Business bank account for deposits</li>
        <li>Business EIN or SSN for tax purposes</li>
        <li>Business address and contact information</li>
        <li>Admin access to your CRM</li>
      </ul>
    </div>

    <h2 id="stripe-account">Creating Your Stripe Account</h2>
    
    <h3>Step 1: Sign Up for Stripe</h3>
    <ol>
      <li>Visit <a href="https://stripe.com" target="_blank">stripe.com</a></li>
      <li>Click "Start now" or "Sign up"</li>
      <li>Enter your email address and create a password</li>
      <li>Verify your email address through the confirmation email</li>
      <li>You'll be redirected to the Stripe Dashboard</li>
    </ol>

    <h3>Step 2: Complete Business Verification</h3>
    <p>Stripe requires business verification to process payments:</p>
    <ol>
      <li><strong>Business Information:</strong>
        <ul>
          <li>Legal business name</li>
          <li>Business type (LLC, Corporation, Sole Proprietorship)</li>
          <li>EIN or business tax ID</li>
          <li>Business address</li>
          <li>Business phone number</li>
        </ul>
      </li>
      <li><strong>Personal Information (for verification):</strong>
        <ul>
          <li>Full legal name</li>
          <li>Date of birth</li>
          <li>Social Security Number (last 4 digits)</li>
          <li>Home address</li>
        </ul>
      </li>
      <li><strong>Banking Information:</strong>
        <ul>
          <li>Bank account number</li>
          <li>Routing number</li>
          <li>Account type (checking/savings)</li>
        </ul>
      </li>
    </ol>

    <div class="bg-yellow-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Important:</strong> Stripe verification typically takes 1-2 business days. You can start testing immediately, but cannot process real payments until verification is complete.
    </div>

    <h2 id="crm-integration">Connecting Stripe to Your CRM</h2>
    
    <h3>Step 1: Access CRM Integration Settings</h3>
    <ol>
      <li>Log in to your CRM at <code>https://app.thedefinedcrm.com</code></li>
      <li>Navigate to <strong>Settings</strong> → <strong>Integrations</strong></li>
      <li>Find the <strong>Stripe</strong> section</li>
      <li>Click <strong>Connect Stripe Account</strong></li>
    </ol>

    <h3>Step 2: Obtain API Keys</h3>
    <p>You'll need to get your Stripe API keys:</p>
    <ol>
      <li>In your Stripe Dashboard, go to <strong>Developers</strong> → <strong>API keys</strong></li>
      <li>You'll see two types of keys:
        <ul>
          <li><strong>Test keys:</strong> For testing (starts with "pk_test" and "sk_test")</li>
          <li><strong>Live keys:</strong> For real payments (starts with "pk_live" and "sk_live")</li>
        </ul>
      </li>
      <li>Copy your <strong>Publishable key</strong> (safe to share)</li>
      <li>Copy your <strong>Secret key</strong> (keep private!)</li>
    </ol>

    <h3>Step 3: Configure in CRM</h3>
    <ol>
      <li>Back in your CRM, paste the Publishable key in the designated field</li>
      <li>Paste the Secret key in its field</li>
      <li>Select your environment:
        <ul>
          <li><strong>Test Mode:</strong> For testing with fake cards</li>
          <li><strong>Live Mode:</strong> For processing real payments</li>
        </ul>
      </li>
      <li>Click <strong>Save Configuration</strong></li>
      <li>Test the connection using the <strong>Test Connection</strong> button</li>
    </ol>

    <h2 id="payment-settings">Configuring Payment Settings</h2>
    
    <h3>Payment Methods</h3>
    <p>Configure which payment methods to accept:</p>
    <ul>
      <li>☑ <strong>Credit Cards:</strong> Visa, Mastercard, American Express, Discover</li>
      <li>☑ <strong>Debit Cards:</strong> All major debit cards</li>
      <li>☑ <strong>ACH Bank Transfers:</strong> Direct bank payments (lower fees)</li>
      <li>☐ <strong>Digital Wallets:</strong> Apple Pay, Google Pay (if supported)</li>
    </ul>

    <h3>Payment Options</h3>
    <p>Set up how customers can pay:</p>
    <ol>
      <li><strong>Invoice Payments:</strong>
        <ul>
          <li>Enable "Pay Now" button on invoices</li>
          <li>Allow partial payments</li>
          <li>Set minimum payment amount</li>
        </ul>
      </li>
      <li><strong>Saved Cards:</strong>
        <ul>
          <li>Allow customers to save cards for future use</li>
          <li>Enable automatic payment retry for failed charges</li>
          <li>Set up PCI-compliant card storage</li>
        </ul>
      </li>
      <li><strong>Recurring Payments:</strong>
        <ul>
          <li>Enable subscription billing</li>
          <li>Set up autopay for regular customers</li>
          <li>Configure payment schedules</li>
        </ul>
      </li>
    </ol>

    <h3>Fee Configuration</h3>
    <p>Understand and configure fee handling:</p>
    <ul>
      <li><strong>Stripe Fees (as of 2024):</strong>
        <ul>
          <li>2.9% + $0.30 per successful card charge</li>
          <li>0.8% for ACH transfers (capped at $5)</li>
          <li>No monthly fees or setup costs</li>
        </ul>
      </li>
      <li><strong>Fee Handling Options:</strong>
        <ul>
          <li>Absorb fees (you pay)</li>
          <li>Pass fees to customer (add to invoice)</li>
          <li>Split fees (share with customer)</li>
        </ul>
      </li>
    </ul>

    <h2 id="customer-portal">Customer Payment Portal</h2>
    
    <h3>Enabling the Payment Portal</h3>
    <ol>
      <li>Go to <strong>Settings</strong> → <strong>Customer Portal</strong></li>
      <li>Enable <strong>Online Payments</strong></li>
      <li>Configure portal options:
        <ul>
          <li>Allow viewing invoices</li>
          <li>Enable payment history</li>
          <li>Show outstanding balance</li>
          <li>Allow downloading receipts</li>
        </ul>
      </li>
    </ol>

    <h3>Payment Page Customization</h3>
    <p>Customize the payment experience:</p>
    <ul>
      <li><strong>Company Information:</strong> Display your business details</li>
      <li><strong>Payment Form Fields:</strong>
        <ul>
          <li>Cardholder name</li>
          <li>Card number</li>
          <li>Expiration date</li>
          <li>CVV/Security code</li>
          <li>Billing ZIP code</li>
        </ul>
      </li>
      <li><strong>Security Badges:</strong> Show SSL and PCI compliance badges</li>
      <li><strong>Thank You Page:</strong> Custom message after payment</li>
    </ul>

    <h2 id="processing-payments">Processing Payments</h2>
    
    <h3>Manual Payment Processing</h3>
    <p>To process a payment manually:</p>
    <ol>
      <li>Open the customer's invoice</li>
      <li>Click <strong>Record Payment</strong></li>
      <li>Select <strong>Credit Card</strong> as payment method</li>
      <li>Enter card details or select saved card</li>
      <li>Enter payment amount</li>
      <li>Click <strong>Process Payment</strong></li>
      <li>Wait for confirmation (usually 2-3 seconds)</li>
      <li>Payment receipt is automatically generated</li>
    </ol>

    <h3>Customer Self-Service Payments</h3>
    <p>How customers pay online:</p>
    <ol>
      <li>Customer receives invoice via email</li>
      <li>Clicks <strong>Pay Now</strong> button</li>
      <li>Redirected to secure payment page</li>
      <li>Enters payment information</li>
      <li>Submits payment</li>
      <li>Receives confirmation email with receipt</li>
      <li>Invoice automatically marked as paid in CRM</li>
    </ol>

    <h3>Payment Confirmation</h3>
    <p>After successful payment:</p>
    <ul>
      <li>Invoice status updates to "Paid"</li>
      <li>Payment record created with transaction ID</li>
      <li>Customer receives email receipt</li>
      <li>Payment appears in your Stripe dashboard</li>
      <li>Funds pending transfer to your bank (1-2 business days)</li>
    </ul>

    <h2 id="recurring-payments">Setting Up Recurring Payments</h2>
    
    <h3>Creating Subscription Plans</h3>
    <ol>
      <li>Go to <strong>Settings</strong> → <strong>Recurring Services</strong></li>
      <li>Click <strong>Create Subscription Plan</strong></li>
      <li>Configure plan details:
        <ul>
          <li>Plan name (e.g., "Weekly Cleaning")</li>
          <li>Billing frequency (weekly, bi-weekly, monthly)</li>
          <li>Amount per period</li>
          <li>Setup fee (if applicable)</li>
          <li>Trial period (optional)</li>
        </ul>
      </li>
      <li>Save the subscription plan</li>
    </ol>

    <h3>Enrolling Customers</h3>
    <p>To set up autopay for a customer:</p>
    <ol>
      <li>Open customer profile</li>
      <li>Go to <strong>Payment Methods</strong> tab</li>
      <li>Add a credit card on file</li>
      <li>Navigate to <strong>Subscriptions</strong></li>
      <li>Select subscription plan</li>
      <li>Set start date</li>
      <li>Enable autopay</li>
      <li>Customer receives confirmation email</li>
    </ol>

    <h3>Managing Subscriptions</h3>
    <ul>
      <li><strong>Pause:</strong> Temporarily stop charges</li>
      <li><strong>Cancel:</strong> End subscription</li>
      <li><strong>Modify:</strong> Change plan or payment method</li>
      <li><strong>Retry Failed Payments:</strong> Automatic retry schedule</li>
    </ul>

    <h2 id="refunds-disputes">Refunds and Disputes</h2>
    
    <h3>Processing Refunds</h3>
    <ol>
      <li>Locate the paid invoice</li>
      <li>Click <strong>More Actions</strong> → <strong>Refund Payment</strong></li>
      <li>Enter refund amount:
        <ul>
          <li>Full refund</li>
          <li>Partial refund (specify amount)</li>
        </ul>
      </li>
      <li>Add refund reason (for records)</li>
      <li>Click <strong>Process Refund</strong></li>
      <li>Refund processed immediately through Stripe</li>
      <li>Customer notified via email</li>
      <li>Funds returned in 5-10 business days</li>
    </ol>

    <h3>Handling Disputes/Chargebacks</h3>
    <p>When a customer disputes a charge:</p>
    <ol>
      <li>You receive email notification from Stripe</li>
      <li>Log into Stripe Dashboard</li>
      <li>Navigate to <strong>Disputes</strong></li>
      <li>Review dispute reason</li>
      <li>Gather evidence:
        <ul>
          <li>Service agreements</li>
          <li>Signed estimates</li>
          <li>Communication records</li>
          <li>Proof of service delivery</li>
        </ul>
      </li>
      <li>Submit evidence through Stripe</li>
      <li>Wait for card network decision (7-21 days)</li>
    </ol>

    <div class="bg-red-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Dispute Prevention Tips:</strong>
      <ul>
        <li>Clear service descriptions on invoices</li>
        <li>Recognizable business name on statements</li>
        <li>Prompt customer service response</li>
        <li>Send receipts immediately after payment</li>
      </ul>
    </div>

    <h2 id="reporting">Payment Reporting</h2>
    
    <h3>CRM Payment Reports</h3>
    <p>Access payment reports in your CRM:</p>
    <ol>
      <li>Go to <strong>Reports</strong> → <strong>Financial Reports</strong></li>
      <li>Select <strong>Payment Report</strong></li>
      <li>Choose date range</li>
      <li>Filter options:
        <ul>
          <li>Payment method (card, ACH, cash, check)</li>
          <li>Payment status (successful, failed, refunded)</li>
          <li>Customer or customer group</li>
          <li>Service type</li>
        </ul>
      </li>
      <li>Export as CSV or PDF</li>
    </ol>

    <h3>Stripe Dashboard Reports</h3>
    <p>Additional reporting in Stripe:</p>
    <ul>
      <li><strong>Balance:</strong> Available and pending funds</li>
      <li><strong>Payments:</strong> All transaction history</li>
      <li><strong>Payouts:</strong> Bank transfer schedule</li>
      <li><strong>Customers:</strong> Saved payment methods</li>
      <li><strong>Radar:</strong> Fraud prevention metrics</li>
    </ul>

    <h2 id="security">Security and Compliance</h2>
    
    <h3>PCI Compliance</h3>
    <p>Stripe handles PCI compliance, but you should:</p>
    <ul>
      <li>Never store card numbers in plain text</li>
      <li>Always use HTTPS for payment pages</li>
      <li>Keep your CRM and browser updated</li>
      <li>Use strong passwords for Stripe and CRM accounts</li>
      <li>Enable two-factor authentication</li>
      <li>Limit access to payment processing features</li>
    </ul>

    <h3>Fraud Prevention</h3>
    <p>Stripe Radar automatically screens for fraud:</p>
    <ul>
      <li><strong>Machine Learning:</strong> Analyzes patterns to detect fraud</li>
      <li><strong>3D Secure:</strong> Additional authentication for high-risk transactions</li>
      <li><strong>Custom Rules:</strong> Set your own risk thresholds</li>
      <li><strong>Block Lists:</strong> Prevent specific cards or emails</li>
    </ul>

    <h2 id="testing">Testing Payment Processing</h2>
    
    <h3>Test Mode Setup</h3>
    <ol>
      <li>Enable test mode in CRM Stripe settings</li>
      <li>Use test API keys (pk_test_ and sk_test_)</li>
      <li>Create test invoices</li>
    </ol>

    <h3>Test Card Numbers</h3>
    <p>Use these Stripe test cards:</p>
    <ul>
      <li><strong>Successful payment:</strong> 4242 4242 4242 4242</li>
      <li><strong>Declined payment:</strong> 4000 0000 0000 0002</li>
      <li><strong>Requires authentication:</strong> 4000 0025 0000 3155</li>
      <li><strong>Expired card:</strong> 4000 0000 0000 0069</li>
    </ul>
    <p>Use any future date for expiration and any 3 digits for CVV.</p>

    <h2 id="troubleshooting">Common Issues and Solutions</h2>
    
    <h3>Payment Failures</h3>
    <table>
      <thead>
        <tr>
          <th>Error</th>
          <th>Cause</th>
          <th>Solution</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Card declined</td>
          <td>Insufficient funds or bank rejection</td>
          <td>Ask customer to contact bank or use different card</td>
        </tr>
        <tr>
          <td>Invalid API key</td>
          <td>Wrong or expired keys</td>
          <td>Re-enter API keys from Stripe dashboard</td>
        </tr>
        <tr>
          <td>Network error</td>
          <td>Connection issues</td>
          <td>Check internet connection, retry</td>
        </tr>
        <tr>
          <td>Processing error</td>
          <td>Stripe service issue</td>
          <td>Wait and retry, check Stripe status page</td>
        </tr>
      </tbody>
    </table>

    <h3>Integration Issues</h3>
    <ul>
      <li><strong>Webhook failures:</strong> 
        <ul>
          <li>Verify webhook endpoint URL</li>
          <li>Check webhook signing secret</li>
          <li>Review webhook logs in Stripe</li>
        </ul>
      </li>
      <li><strong>Payment not updating invoice:</strong>
        <ul>
          <li>Check CRM-Stripe connection</li>
          <li>Verify webhook configuration</li>
          <li>Manual sync if needed</li>
        </ul>
      </li>
    </ul>

    <h2 id="best-practices">Best Practices</h2>
    
    <h3>Optimizing Payment Collection</h3>
    <ul>
      <li>✓ Send invoices promptly after service</li>
      <li>✓ Include clear "Pay Now" buttons</li>
      <li>✓ Offer multiple payment options</li>
      <li>✓ Set up payment reminders</li>
      <li>✓ Keep payment forms simple</li>
      <li>✓ Display security badges</li>
    </ul>

    <h3>Customer Experience</h3>
    <ul>
      <li>Make payment process frictionless</li>
      <li>Save customer cards (with permission)</li>
      <li>Send immediate payment confirmations</li>
      <li>Provide clear receipts</li>
      <li>Offer payment plans for large amounts</li>
    </ul>

    <h3>Financial Management</h3>
    <ul>
      <li>Reconcile payments daily</li>
      <li>Monitor failed payment patterns</li>
      <li>Track payment processing fees</li>
      <li>Review disputes promptly</li>
      <li>Keep accurate records for taxes</li>
    </ul>

    <div class="bg-green-50 p-4 rounded-lg mt-6">
      <strong>Pro Tip:</strong> Enable Stripe's automatic receipts and customize them with your branding for a professional touch. This saves time and ensures customers always have payment documentation.
    </div>

    <h2 id="support">Getting Help</h2>
    
    <h3>Stripe Support</h3>
    <ul>
      <li><strong>Documentation:</strong> stripe.com/docs</li>
      <li><strong>Email Support:</strong> Available 24/7 for all accounts</li>
      <li><strong>Chat Support:</strong> For accounts processing over $5k/month</li>
      <li><strong>Phone Support:</strong> For high-volume accounts</li>
    </ul>

    <h3>CRM Support</h3>
    <ul>
      <li><strong>Email:</strong> support@definedcleaning.com</li>
      <li><strong>Phone:</strong> (123) 456-7890</li>
      <li><strong>Hours:</strong> Monday-Friday, 8 AM - 6 PM PST</li>
    </ul>
  `,
  tableOfContents: [
    { id: 'stripe-account', title: 'Creating Your Stripe Account' },
    { id: 'crm-integration', title: 'Connecting Stripe to Your CRM' },
    { id: 'payment-settings', title: 'Configuring Payment Settings' },
    { id: 'customer-portal', title: 'Customer Payment Portal' },
    { id: 'processing-payments', title: 'Processing Payments' },
    { id: 'recurring-payments', title: 'Setting Up Recurring Payments' },
    { id: 'refunds-disputes', title: 'Refunds and Disputes' },
    { id: 'reporting', title: 'Payment Reporting' },
    { id: 'security', title: 'Security and Compliance' },
    { id: 'testing', title: 'Testing Payment Processing' },
    { id: 'troubleshooting', title: 'Common Issues and Solutions' },
    { id: 'best-practices', title: 'Best Practices' },
    { id: 'support', title: 'Getting Help' }
  ],
  relatedArticles: [
    'invoicing-billing-guide',
    'converting-estimates',
    'recurring-invoices',
    'customer-portal-setup'
  ]
};